import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams, createSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useDebounceCallback } from 'usehooks-ts';
import { Box, Flex, SimpleGrid, Spinner, Text, Card, CardBody } from '@chakra-ui/react';
import { useCompanies, ICompany } from '../../../../../hooks/useCompanies';
import { Paginator } from '../../../../../components/paginator';
import { SearchModeBar, MODES_ID_NOME_CNPJ } from '../../../../../components/SearchModeBar';
import { formatCNPJ } from '../../../../../utils/FormatFunctions';
import { IData } from '../../../../../types/shared';

interface IcmsCompanyPickerProps {
  basePath: string;
  emptyMsg?: string;
}

type SearchMode = 'Nome' | 'CNPJ' | 'ID';

const PAGE_SIZE = 24;

export function IcmsCompanyPicker({
  basePath,
  emptyMsg = 'Nenhuma empresa encontrada.',
}: IcmsCompanyPickerProps) {
  const navigate = useNavigate();
  const { getCompanies } = useCompanies();

  const [searchParams, setSearchParams] = useSearchParams(
    createSearchParams({ page: '1', size: String(PAGE_SIZE) }),
  );

  const searchMode = (searchParams.get('search_mode') as SearchMode) || 'Nome';
  const searchValueParam = searchParams.get('q') || '';
  const page = parseInt(searchParams.get('page') || '1', 10);

  const [searchInput, setSearchInput] = useState(searchValueParam);

  useEffect(() => {
    setSearchInput(searchValueParam);
  }, [searchValueParam]);

  const debounced = useDebounceCallback((value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set('q', value);
    else next.delete('q');
    next.set('page', '1');
    setSearchParams(next, { replace: true });
  }, 350);

  const filters = useMemo(() => {
    const params: Record<string, string | number> = { page, size: PAGE_SIZE };
    if (searchValueParam) {
      if (searchMode === 'CNPJ') params.cnpj = searchValueParam;
      else if (searchMode === 'ID') params.id = searchValueParam;
      else params.search = searchValueParam;
    }
    return params;
  }, [page, searchValueParam, searchMode]);

  const { data, isFetching } = useQuery<IData<ICompany[]>>({
    queryKey: ['icms-company-picker', filters],
    queryFn: getCompanies,
    placeholderData: prev => prev,
  });

  const items = data?.data?.items ?? [];
  const pagination = {
    total: data?.data?.total ?? 0,
    pages: data?.data?.pages ?? 0,
    page: data?.data?.page ?? 1,
    size: data?.data?.size ?? PAGE_SIZE,
    next_page: data?.data?.next_page ?? null,
    previous_page: data?.data?.previous_page ?? null,
  };

  return (
    <Box>
      <Flex gap={2} mb={4} wrap="wrap" align="center">
        <SearchModeBar
          modes={MODES_ID_NOME_CNPJ}
          mode={searchMode}
          onModeChange={m => {
            const next = new URLSearchParams(searchParams);
            next.set('search_mode', m);
            next.set('page', '1');
            setSearchParams(next, { replace: true });
          }}
          value={searchInput}
          onValueChange={v => {
            setSearchInput(v);
            debounced(v);
          }}
        />
        {isFetching && <Spinner size="sm" color="primary" />}
      </Flex>

      {items.length === 0 && !isFetching ? (
        <Flex justify="center" align="center" minH="200px">
          <Text color="gray.500">{emptyMsg}</Text>
        </Flex>
      ) : (
        <SimpleGrid columns={{ base: 1, md: 2, lg: 3, xl: 4 }} spacing={3}>
          {items.map(company => (
            <Card
              key={company.id}
              cursor="pointer"
              transition="all 0.2s"
              _hover={{ transform: 'translateY(-2px)', boxShadow: 'md', borderColor: 'blue.300' }}
              borderWidth="1px"
              onClick={() => navigate(`${basePath}/${company.id}`)}
            >
              <CardBody p={3}>
                <Text fontWeight="bold" fontSize="13px" noOfLines={1}>
                  {company.razao_social || company.nome || `Empresa ${company.id}`}
                </Text>
                <Text fontSize="11px" color="gray.500">
                  CNPJ: {company.cnpj ? formatCNPJ(company.cnpj) : '—'}
                </Text>
                <Text fontSize="11px" color="gray.500">
                  ID: {company.id}
                  {company.uf ? ` · ${company.uf}` : ''}
                </Text>
              </CardBody>
            </Card>
          ))}
        </SimpleGrid>
      )}

      {pagination.total > PAGE_SIZE && (
        <Box mt={4}>
          <Paginator pagination={pagination} />
        </Box>
      )}
    </Box>
  );
}
